import { IncomingMessage } from 'http';
import { URL } from 'url';

import {
    DataDomeEvent,
    DataDomeRequest,
    Response,
    ResponseError,
    ResponseAction,
    ResponseStatus,
    RequestMetadata,
} from './model';
import { DATADOME_ENDPOINT, DATADOME_TIMEOUT } from './constants';

export interface LogInterface {
    debug(primaryMessage: string, ...supportingData: unknown[]): void;
    warn(primaryMessage: string, ...supportingData: unknown[]): void;
    error(primaryMessage: string, ...supportingData: unknown[]): void;
    info(primaryMessage: string, ...supportingData: unknown[]): void;
}

export interface DataDomeOptions {
    logger?: LogInterface;
    timeout?: number;
    endpoint?: string;
}

function getDefaultOptions(): Required<DataDomeOptions> {
    return {
        logger: console,
        timeout: DATADOME_TIMEOUT,
        endpoint: DATADOME_ENDPOINT,
    };
}

export enum Operation {
    VALIDATE = 'validate',
    COLLECT = 'collect',
}

/**
 * Class to instantiate a DataDome client
 */
export class DataDome {
    private endpoint: string;
    private logger: LogInterface;
    private timeout: number;
    private requestOptions: RequestInit;
    constructor(key: string, options: DataDomeOptions = {}) {
        const defaultOptions = getDefaultOptions();
        const finalParameters = Object.assign({}, defaultOptions, options);
        const { endpoint, logger, timeout } = finalParameters;

        this.endpoint = endpoint;
        this.timeout = timeout;
        this.logger = logger;

        this.requestOptions = {
            keepalive: true,
            headers: {
                Accept: 'application/json',
                Connection: 'keep-alive',
                'Content-Type': 'application/json',
                'x-api-key': key,
            },
            method: 'POST',
        };

        if (
            !this.endpoint.toLowerCase().startsWith('https://') &&
            !this.endpoint.toLowerCase().startsWith('http://')
        ) {
            this.logger.warn(
                `Endpoint ${this.endpoint} is not a valid URL. Defaulting to ${defaultOptions.endpoint}`
            );
            this.endpoint = defaultOptions.endpoint;
        }
    }

    private buildPayload(
        request: IncomingMessage,
        event: DataDomeEvent,
        requestMetadata?: RequestMetadata
    ): DataDomeRequest {
        return event.mergeWith(new DataDomeRequest(request, requestMetadata));
    }

    /**
     * Perform the request to the Protect API
     */
    private async sendRequest({
        operation,
        request,
        requestMetadata,
        event,
        args,
    }: {
        operation: Operation;
        request: IncomingMessage;
        requestMetadata?: RequestMetadata;
        event: DataDomeEvent;
        args?: { controller: AbortController };
    }) {
        // Ensure this.endpoint does not have a trailing slash
        const endpoint = this.endpoint.endsWith('/') ? this.endpoint.slice(0, -1) : this.endpoint;
        const url = new URL(endpoint + '/v1/' + operation + '/' + event.action);
        const payload = this.buildPayload(request, event, requestMetadata);

        this.logger.debug(`url: ${url}`);
        this.logger.debug(`body: ${JSON.stringify(payload)}`);

        const requestConfig: RequestInit = {
            ...this.requestOptions,
            body: JSON.stringify(payload),
            signal: args?.controller.signal,
        };
        return fetch(url.toString(), requestConfig);
    }

    /**
     * Validate request
     */
    async validate(
        request: IncomingMessage,
        event: DataDomeEvent,
        requestMetadata?: RequestMetadata
    ): Promise<Response | ResponseError> {
        const controller = new AbortController();
        const timeoutId = setTimeout(
            () => controller.abort('DataDome Fraud API timeout'),
            this.timeout
        );
        try {
            const response = await this.sendRequest({
                operation: Operation.VALIDATE,
                request,
                requestMetadata,
                event,
                args: {
                    controller,
                },
            });
            const result = await response?.json();
            if (response?.ok) {
                result.status = ResponseStatus.OK;
            } else {
                result.status = ResponseStatus.FAILURE;
                result.action = ResponseAction.ALLOW;
                this.logger.error(`Error on API response: ${JSON.stringify(result)}`);
            }
            return result;
        } catch (error) {
            const isAbortError =
                (error as Error)?.name === 'AbortError' ||
                (error as Error)?.name === 'TimeoutError' ||
                controller.signal.aborted;

            if (isAbortError) {
                return {
                    action: ResponseAction.ALLOW,
                    status: ResponseStatus.TIMEOUT,
                    message: `Request timed out after ${this.timeout} milliseconds`,
                } as ResponseError;
            } else {
                return {
                    action: ResponseAction.ALLOW,
                    status: ResponseStatus.FAILURE,
                    message: `Request failed :${error}`,
                } as ResponseError;
            }
        } finally {
            clearTimeout(timeoutId);
        }
    }

    /**
     * Collect request
     */
    async collect(
        request: IncomingMessage,
        event: DataDomeEvent,
        requestMetadata?: RequestMetadata
    ): Promise<Response | ResponseError> {
        const response = await this.sendRequest({
            operation: Operation.COLLECT,
            request,
            requestMetadata,
            event,
        });
        if (response.ok) {
            return { status: ResponseStatus.OK } as Response;
        } else {
            const json = await response.json();
            return { status: ResponseStatus.FAILURE, ...json } as ResponseError;
        }
    }
}
